import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, BadgeCheck, Filter } from "lucide-react";
import { fetchTestimonials } from "../lib/api";
import { Reveal } from "../components/Reveal";

const CATEGORIES = [
  { id: "all", label: "All Stories" },
  { id: "transformation", label: "Transformations" },
  { id: "confidence", label: "Confidence" },
  { id: "energy", label: "Energy & Drive" },
  { id: "libido", label: "Libido & Performance" },
];

const Avatar = ({ name, photo }) => {
  const initials = name
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        className="w-14 h-14 object-cover border border-white/10"
      />
    );
  }
  return (
    <div className="w-14 h-14 bg-[#D10000]/15 text-[#D10000] border border-[#D10000]/30 flex items-center justify-center font-display tracking-wider text-lg">
      {initials}
    </div>
  );
};

export default function Reviews() {
  const [data, setData] = useState({ items: [], stats: null });
  const [active, setActive] = useState("all");

  useEffect(() => {
    fetchTestimonials().then(setData).catch(() => {});
  }, []);

  const filtered = useMemo(
    () =>
      active === "all"
        ? data.items
        : data.items.filter((i) => i.category === active),
    [active, data.items]
  );

  return (
    <div className="bg-[#050505] text-white" data-testid="reviews-page">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 border-b border-white/10">
        <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
          THE PROOF
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <h1
            className="lg:col-span-7 font-display uppercase leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
          >
            Real men. <br />
            <span className="text-stroke">Real results.</span>
          </h1>
          {data.stats && (
            <div className="lg:col-span-5 grid grid-cols-3 gap-px bg-white/5">
              <Stat value={data.stats.rating_avg.toFixed(1)} label="Avg rating" />
              <Stat
                value={`${data.stats.rating_count.toLocaleString()}+`}
                label="Reviews"
              />
              <Stat value={`${data.stats.reorder_pct}%`} label="Reorder rate" />
            </div>
          )}
        </div>
      </section>

      {/* Filter pills */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 border-b border-white/10">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="h-4 w-4 text-white/40" />
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              data-testid={`reviews-filter-${c.id}`}
              className={`font-display tracking-widest uppercase text-xs px-4 py-2 border transition-colors ${
                active === c.id
                  ? "border-[#D10000] bg-[#D10000]/10 text-[#D10000]"
                  : "border-white/15 text-white/60 hover:border-white hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r, i) => (
            <Reveal key={r.id} delay={(i % 6) * 0.05}>
              <ReviewCard r={r} />
            </Reveal>
          ))}
        </div>
        {filtered.length === 0 && (
          <p className="text-white/40 mt-12">No reviews in this category yet.</p>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="bg-[#0A0A0A] border border-white/10 p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
              JOIN THEM
            </div>
            <h3
              className="font-display uppercase leading-[0.9]"
              style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
            >
              Your story is next.
            </h3>
          </div>
          <Link
            to="/shop"
            data-testid="reviews-shop-cta"
            className="bg-[#D10000] hover:bg-[#A30000] text-white font-display text-lg tracking-widest uppercase px-8 py-4 transition-colors"
          >
            Shop Primenax
          </Link>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ value, label }) => (
  <div className="bg-[#0A0A0A] p-6 text-center">
    <div className="font-display text-3xl md:text-4xl tracking-wider leading-none">
      {value}
    </div>
    <div className="font-display text-[10px] tracking-[0.3em] text-white/40 mt-2 uppercase">
      {label}
    </div>
  </div>
);

export const ReviewCard = ({ r }) => (
  <article
    className="bg-[#0A0A0A] border border-white/10 hover:border-[#D10000]/40 transition-colors flex flex-col h-full"
    data-testid={`review-card-${r.id}`}
  >
    {r.photo && (
      <div className="aspect-[4/5] overflow-hidden border-b border-white/10">
        <img
          src={r.photo}
          alt={r.name}
          className="w-full h-full object-cover"
        />
      </div>
    )}
    <div className="p-6 md:p-8 flex-1 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-0.5">
          {[...Array(r.stars)].map((_, k) => (
            <Star key={k} className="h-4 w-4 fill-[#D10000] text-[#D10000]" />
          ))}
        </div>
        {r.verified && (
          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-emerald-400/80">
            <BadgeCheck className="h-3 w-3" /> Verified
          </span>
        )}
      </div>
      <h3 className="font-display text-xl md:text-2xl tracking-tight uppercase leading-tight mb-3">
        "{r.title}"
      </h3>
      <p className="text-white/70 leading-relaxed text-sm flex-1">{r.quote}</p>
      <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-4">
        <Avatar name={r.name} photo={r.photo && null /* avoid double */} />
        <div className="min-w-0">
          <div className="font-display tracking-wider uppercase text-sm">
            {r.name}{r.age && <span className="text-white/40">, {r.age}</span>}
          </div>
          <div className="text-white/40 text-xs truncate">
            {r.location} · {r.product} · Week {r.weeks}
          </div>
        </div>
      </div>
    </div>
  </article>
);
